import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// Tipos
interface ProductVariant {
  id: string;
  sku: string;
  name: string;
  stock_quantity: number;
  price_adjustment: string;
  attributes: Record<string, unknown>;
  final_price: string;
  final_regular_price?: string;
  is_active?: boolean;
  // Campos de completude
  weight?: number;
  ncm?: string;
  ean?: string;
}

interface PlatformIntegration {
  platform_name: string;
  platform_slug: string;
  icon_url: string;
  color: string;
  is_source: boolean;
}

interface Product {
  id: string;
  name: string;
  reference: string;
  variants?: ProductVariant[];
  base_price: number;
  total_stock?: number;
  status: string;
  variant_count?: number;
  product_type?: 'simple' | 'variable';
  images?: Array<{ id: string; url: string; is_primary?: boolean }>;
  platform_integrations?: PlatformIntegration[];
  // Campos de completude do cadastro
  weight?: number;
  ncm?: string;
  ean?: string;
}

interface ProductVariantsProps {
  product: Product;
  onUpdateVariant?: (variantId: string, updates: { price?: number; stock?: number }) => void;
  onBulkUpdatePrice?: (productId: string, price: number) => void;
  onBulkUpdateStock?: (productId: string, stock: number) => void;
  isSelected?: boolean;
  onToggleSelect?: (productId: string) => void;
}

interface EditingState {
  variantId: string;
  field: 'price' | 'stock';
  value: string;
}

const PlatformIcon: React.FC<{ pi: PlatformIntegration; imgSrc: string }> = ({ pi, imgSrc }) => {
  const [imgError, setImgError] = React.useState(false);
  return (
    <span
      title={`${pi.platform_name}${pi.is_source ? ' (origem)' : ''}`}
      className={`inline-flex items-center justify-center w-6 h-6 rounded-full cursor-default ${
        pi.is_source ? 'ring-2 ring-blue-400' : ''
      }`}
    >
      {!imgError ? (
        <img
          src={imgSrc}
          alt={pi.platform_name}
          className="w-5 h-5 rounded-full object-contain"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="text-white text-[10px] font-bold">
          {pi.platform_name.charAt(0).toUpperCase()}
        </span>
      )}
    </span>
  );
};

const ProductVariants: React.FC<ProductVariantsProps> = ({
  product,
  onUpdateVariant,
  onBulkUpdatePrice,
  onBulkUpdateStock,
  isSelected = false,
  onToggleSelect,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editing, setEditing] = useState<EditingState | null>(null);

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleStartEdit = (variantId: string, field: 'price' | 'stock', currentValue: number) => {
    setEditing({
      variantId,
      field,
      value: currentValue.toString(),
    });
  };

  const handleSaveEdit = () => {
    if (!editing || !onUpdateVariant) return;

    const numericValue = parseFloat(editing.value);
    if (isNaN(numericValue) || numericValue < 0) return;

    onUpdateVariant(editing.variantId, {
      [editing.field]: numericValue,
    });

    setEditing(null);
  };

  const handleCancelEdit = () => {
    setEditing(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Preço De/Por: De riscado quando maior que o Por; preço único quando iguais
  const PrecoDePor: React.FC<{ variant: ProductVariant }> = ({ variant }) => {
    const por = parseFloat(variant.final_price);
    const de = parseFloat(variant.final_regular_price || variant.final_price);
    return (
      <span className="text-sm font-medium text-gray-900">
        {de > por && (
          <span className="text-xs text-gray-400 line-through mr-1">
            {formatPrice(de)}
          </span>
        )}
        {formatPrice(por)}
      </span>
    );
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const isDisplayableAttributeValue = (
    value: unknown
  ): value is string | number | boolean =>
    ['string', 'number', 'boolean'].includes(typeof value);

  // Garantir que variants existe e é um array
  const variants = product.variants || [];
  // product_type vem da plataforma de origem (Bling/Shoppub/etc) e nao deve
  // ser inferido por contagem: 1 variante real (com atributo) e diferente
  // de 1 variante-padrao interna de produto simples. Fallback pra produtos
  // sem o campo ainda (nao reprocessados): trata qualquer variante existente
  // como variacao real, igual a tela de detalhe.
  const isVariableProduct = product.product_type
    ? product.product_type === 'variable'
    : variants.length > 0;
  const isActive = product.status === 'active';
  const defaultVariant = variants[0] || {
    id: 'default',
    sku: product.reference || '',
    name: 'Única',
    stock_quantity: product.total_stock || 0,
    price_adjustment: '0',
    attributes: {},
    final_price: product.base_price?.toString() || '0',
    is_active: isActive
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      {/* Header do produto */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Checkbox de seleção */}
          {onToggleSelect && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect(product.id)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          )}

          {/* Botão de expansão (apenas se há múltiplas variações) */}
          {isVariableProduct && (
            <button
              onClick={handleToggleExpanded}
              className="flex items-center space-x-1 text-gray-600 hover:text-gray-900"
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
          )}

          {/* Imagem do produto */}
          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-md overflow-hidden">
            {product.images && product.images.length > 0 ? (
              <img
                src={product.images.find(img => img.is_primary)?.url || product.images[0]?.url}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400 text-xs">Erro</div>';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </div>

          {/* Informações básicas do produto */}
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <Link
                to={`/products/${product.id}`}
                className="font-medium text-gray-900 hover:text-indigo-600 hover:underline"
                title="Ver detalhe do produto"
              >
                {product.reference}
              </Link>
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  product.status === 'divergence' ? 'bg-red-100 text-red-800 font-bold' :
                  product.status === 'active' ? 'bg-green-100 text-green-800' :
                  product.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                  product.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}
                title={product.status === 'divergence' ?
                  'Produto com incompatibilidade de grade no Tiny. Delete manualmente o produto no Tiny antes de sincronizar.' :
                  ''
                }
              >
                {product.status === 'divergence' ? 'Divergência' :
                 product.status === 'active' ? 'Ativo' :
                 product.status === 'inactive' ? 'Inativo' :
                 product.status === 'draft' ? 'Rascunho' :
                 product.status === 'archived' ? 'Arquivado' :
                 product.status}
              </span>
              
              {/* Badge de imagens */}
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  product.images && product.images.length > 0
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {product.images && product.images.length > 0 ? 'Imagens' : 'Sem imagens'}
              </span>
              
              {/* Badge de variações */}
              {isVariableProduct && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-full">
                  {variants.length} variações
                </span>
              )}

              {/* Icones de plataformas integradas */}
              {product.platform_integrations && product.platform_integrations.length > 0 && (
                <div className="flex items-center space-x-1 ml-1">
                  {product.platform_integrations.map((pi) => {
                    const staticImg = `/images/platforms/${pi.platform_slug}.png`;
                    const imgSrc = pi.icon_url || staticImg;
                    return (
                      <PlatformIcon key={pi.platform_slug} pi={pi} imgSrc={imgSrc} />
                    );
                  })}
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">{product.name}</p>

            {/* Linha de campos de completude (apenas para produtos simples) */}
            {!isVariableProduct && (
              <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                <span className={product.weight ? 'text-green-600' : 'text-red-500'}>
                  Peso: {product.weight ? `${product.weight}g` : 'N/A'}
                </span>
                <span className={product.ncm ? 'text-green-600' : 'text-red-500'}>
                  NCM: {product.ncm || 'N/A'}
                </span>
                <span className={product.ean ? 'text-green-600' : 'text-red-500'}>
                  EAN: {product.ean || 'N/A'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Resumo de preço e estoque (produto como um todo) */}
        <div className="flex items-center space-x-6">
          {isVariableProduct ? (
            <>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  {formatPrice(Math.min(...variants.map(v => parseFloat(v.final_price))))} - {formatPrice(Math.max(...variants.map(v => parseFloat(v.final_price))))}
                </div>
                <div className="text-xs text-gray-500">Preço</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  {product.total_stock}
                </div>
                <div className="text-xs text-gray-500">Estoque Total</div>
              </div>
            </>
          ) : (
            <>
              <div className="text-right">
                <div className="flex items-center space-x-2">
                  {editing?.variantId === defaultVariant.id && editing.field === 'price' ? (
                    <div className="flex items-center space-x-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editing.value}
                        onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                        onKeyDown={handleKeyPress}
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                      <button onClick={handleSaveEdit} className="text-green-600 hover:text-green-800">
                        <CheckIcon className="h-4 w-4" />
                      </button>
                      <button onClick={handleCancelEdit} className="text-red-600 hover:text-red-800">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <PrecoDePor variant={defaultVariant} />
                      {onUpdateVariant && (
                        <button
                          onClick={() => handleStartEdit(defaultVariant.id, 'price', parseFloat(defaultVariant.final_price))}
                          className="text-gray-400 hover:text-blue-600"
                        >
                          <PencilIcon className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-500">Preço</div>
              </div>
              <div className="text-right">
                <div className="flex items-center space-x-2">
                  {editing?.variantId === defaultVariant.id && editing.field === 'stock' ? (
                    <div className="flex items-center space-x-1">
                      <input
                        type="number"
                        min="0"
                        value={editing.value}
                        onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                        onKeyDown={handleKeyPress}
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                      <button onClick={handleSaveEdit} className="text-green-600 hover:text-green-800">
                        <CheckIcon className="h-4 w-4" />
                      </button>
                      <button onClick={handleCancelEdit} className="text-red-600 hover:text-red-800">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <span className="text-sm font-medium text-gray-900">
                        {defaultVariant.stock_quantity}
                      </span>
                      {onUpdateVariant && (
                        <button
                          onClick={() => handleStartEdit(defaultVariant.id, 'stock', defaultVariant.stock_quantity)}
                          className="text-gray-400 hover:text-blue-600"
                        >
                          <PencilIcon className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-500">Estoque</div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lista de variações expandida */}
      {isVariableProduct && isExpanded && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <div className="space-y-2">
            {variants.map((variant) => (
              <div
                key={variant.id}
                className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md"
              >
                <div className="flex items-center space-x-3">
                  <div className="text-sm font-mono text-gray-600">{variant.sku}</div>
                  <div className="flex items-center space-x-2">
                    {Object.entries(variant.attributes)
                      .filter(
                        ([key, value]) =>
                          !key.startsWith('_') && isDisplayableAttributeValue(value)
                      )
                      .map(([key, value]) => (
                      <span
                        key={key}
                        className="bg-white px-2 py-1 rounded text-xs text-gray-600 border"
                      >
                        {key}: {String(value)}
                      </span>
                    ))}
                  </div>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      variant.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {variant.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                  {/* Campos de completude da variacao */}
                  <span className={variant.weight ? 'text-green-600 text-xs' : 'text-red-500 text-xs'}>
                    Peso: {variant.weight ? `${variant.weight}g` : 'N/A'}
                  </span>
                  <span className={variant.ncm ? 'text-green-600 text-xs' : 'text-red-500 text-xs'}>
                    NCM: {variant.ncm || 'N/A'}
                  </span>
                  <span className={variant.ean ? 'text-green-600 text-xs' : 'text-red-500 text-xs'}>
                    EAN: {variant.ean || 'N/A'}
                  </span>
                </div>

                <div className="flex items-center space-x-4">
                  {/* Preço da variação */}
                  <div className="flex items-center space-x-2">
                    {editing?.variantId === variant.id && editing.field === 'price' ? (
                      <div className="flex items-center space-x-1">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editing.value}
                          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                          onKeyDown={handleKeyPress}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        <button onClick={handleSaveEdit} className="text-green-600 hover:text-green-800">
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button onClick={handleCancelEdit} className="text-red-600 hover:text-red-800">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1">
                        <PrecoDePor variant={variant} />
                        {onUpdateVariant && (
                          <button
                            onClick={() => handleStartEdit(variant.id, 'price', parseFloat(variant.final_price))}
                            className="text-gray-400 hover:text-blue-600"
                          >
                            <PencilIcon className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Estoque da variação */}
                  <div className="flex items-center space-x-2">
                    {editing?.variantId === variant.id && editing.field === 'stock' ? (
                      <div className="flex items-center space-x-1">
                        <input
                          type="number"
                          min="0"
                          value={editing.value}
                          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                          onKeyDown={handleKeyPress}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        <button onClick={handleSaveEdit} className="text-green-600 hover:text-green-800">
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button onClick={handleCancelEdit} className="text-red-600 hover:text-red-800">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1">
                        <span className="text-sm font-medium text-gray-900">
                          {variant.stock_quantity} un
                        </span>
                        {onUpdateVariant && (
                          <button
                            onClick={() => handleStartEdit(variant.id, 'stock', variant.stock_quantity)}
                            className="text-gray-400 hover:text-blue-600"
                          >
                            <PencilIcon className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductVariants;
